import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import './App.css';

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || getSystemTheme());

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Follow system changes only when the user hasn't set an explicit preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (!localStorage.getItem('theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      return next;
    });
  };

  return [theme, toggleTheme];
}

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function isTokenValid(token) {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload) return false;
  return payload.exp * 1000 > Date.now();
}

// Component that handles the ?token= redirect from backend
function TokenHandler({ onAuth }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    const token = searchParams.get('token');
    if (!token) return;
    handledRef.current = true;
    localStorage.setItem('token', token);
    onAuth(parseJwt(token));
    // Clean token from URL without re-render loop
    window.history.replaceState({}, document.title, '/');
    navigate('/', { replace: true });
  }, [searchParams, onAuth, navigate]);

  return null;
}

// Logout page: clear token and redirect to backend /logout
function LogoutPage() {
  useEffect(() => {
    localStorage.removeItem('token');
    const backendUrl = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
    window.location.href = `${backendUrl}/logout`;
  }, []);
  return <div className="loading">Kilépés...</div>;
}

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    if (isTokenValid(token)) return parseJwt(token);
    localStorage.removeItem('token');
    return null;
  });
  const [theme, toggleTheme] = useTheme();

  const handleAuth = (userData) => setUser(userData);

  return (
    <BrowserRouter>
      <TokenHandler onAuth={handleAuth} />
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute user={user}>
              <Dashboard user={user} theme={theme} onThemeToggle={toggleTheme} />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
